import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Instructor {
  id: string;
  instructor_user_id?: string;
  manual_name?: string;
  name?: string;
  username?: string;
  avatar_url?: string;
  belt_rank?: string;
  is_manual: boolean;
  created_at: string;
}

interface UseProfileInstructorsReturn {
  instructors: Instructor[];
  loading: boolean;
  error: string | null;
  addInstructor: (instructor_user_id?: string, manual_name?: string) => Promise<void>;
  removeInstructor: (instructor_user_id?: string, manual_name?: string) => Promise<void>;
  refetch: () => Promise<void>;
}

function capitalizeWords(str: string): string {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function useProfileInstructors(profileId: string | undefined): UseProfileInstructorsReturn {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInstructors = useCallback(async () => {
    if (!profileId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('profile_instructors')
        .select(`
          id,
          instructor_user_id,
          manual_name,
          created_at,
          profiles!profile_instructors_instructor_user_id_fkey (
            id,
            full_name,
            username,
            profile_picture_url,
            belt_rank
          )
        `)
        .eq('profile_id', profileId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      const formatted: Instructor[] = (data || []).map((row: any) => {
        if (row.instructor_user_id && row.profiles) {
          return {
            id: row.id,
            instructor_user_id: row.instructor_user_id,
            name: row.profiles.full_name || 'Unknown',
            username: row.profiles.username || 'unknown',
            avatar_url: row.profiles.profile_picture_url,
            belt_rank: row.profiles.belt_rank,
            is_manual: false,
            created_at: row.created_at,
          };
        }
        return {
          id: row.id,
          manual_name: row.manual_name,
          is_manual: true,
          created_at: row.created_at,
        };
      });

      setInstructors(formatted);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch instructors';
      setError(msg);
      console.error('Error fetching instructors:', err);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  const addInstructor = useCallback(async (instructor_user_id?: string, manual_name?: string) => {
    if (!profileId) return;

    try {
      // Build the check query to avoid duplicates
      const checkQuery: Record<string, string> = { profile_id: profileId };
      if (instructor_user_id) {
        checkQuery.instructor_user_id = instructor_user_id;
      } else if (manual_name) {
        checkQuery.manual_name = capitalizeWords(manual_name);
      } else {
        throw new Error('Either instructor_user_id or manual_name is required');
      }

      const { data: existing } = await supabase
        .from('profile_instructors')
        .select('id')
        .match(checkQuery)
        .maybeSingle();

      if (!existing) {
        const insertData: Record<string, string> = { profile_id: profileId };
        if (instructor_user_id) {
          insertData.instructor_user_id = instructor_user_id;
        } else {
          insertData.manual_name = capitalizeWords(manual_name!);
        }

        const { error: insertError } = await supabase
          .from('profile_instructors')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      await fetchInstructors();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add instructor';
      setError(msg);
      console.error('Error adding instructor:', err);
      throw err;
    }
  }, [profileId, fetchInstructors]);

  const removeInstructor = useCallback(async (instructor_user_id?: string, manual_name?: string) => {
    if (!profileId) return;

    try {
      const deleteQuery: Record<string, string> = { profile_id: profileId };
      if (instructor_user_id) {
        deleteQuery.instructor_user_id = instructor_user_id;
      } else if (manual_name) {
        deleteQuery.manual_name = manual_name;
      } else {
        throw new Error('Either instructor_user_id or manual_name is required');
      }

      const { error: deleteError } = await supabase
        .from('profile_instructors')
        .delete()
        .match(deleteQuery);

      if (deleteError) throw deleteError;

      await fetchInstructors();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove instructor';
      setError(msg);
      console.error('Error removing instructor:', err);
      throw err;
    }
  }, [profileId, fetchInstructors]);

  useEffect(() => {
    fetchInstructors();
  }, [fetchInstructors]);

  return {
    instructors,
    loading,
    error,
    addInstructor,
    removeInstructor,
    refetch: fetchInstructors,
  };
}
